import { useEffect, useState } from "react";
import { ArrowUpRight, ShieldCheck, Star, Layers, MapPin, Wallet } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brokerGoUrl } from "@/lib/brokers";
import Seo from "@/components/Seo";

export default function Brokers() {
  const { user } = useAuth();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/brokers").then((r) => setBrokers(r.data || [])).catch(() => setBrokers([])).finally(() => setLoading(false));
  }, []);

  const sorted = [...brokers].sort((a, b) => (b.recommended ? 1 : 0) - (a.recommended ? 1 : 0));

  return (
    <div className="mx-auto max-w-6xl px-5 py-14 lg:px-8 lg:py-20">
      <Seo title="Recommended brokers" description="The MT4/MT5 brokers we trust to run our EAs and indicators. Open a live account and start trading with tight spreads." />
      <p className="overline mb-3">Brokers</p>
      <h1 className="font-editorial text-4xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-5xl">
        Brokers we trust to run our tools.
      </h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-[color:var(--ink-body)]">
        Our EAs and indicators run on MT4/MT5. These are the brokers we use and recommend — tight spreads,
        fast execution and EA-friendly. Pick one that supports your region and fund a live account to get started.
      </p>

      {loading ? (
        <p className="mt-10 text-sm text-[color:var(--ink-muted)]">Loading brokers…</p>
      ) : sorted.length === 0 ? (
        <p className="mt-10 text-sm text-[color:var(--ink-muted)]">No brokers listed yet — check back soon.</p>
      ) : (
        <div className="mt-10 grid gap-5 md:grid-cols-2" data-testid="brokers-grid">
          {sorted.map((b) => (
            <div key={b.slug} data-testid={`broker-card-${b.slug}`}
              className="flex flex-col rounded-xl border border-[color:var(--ink-hairline)] bg-white p-6">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  {b.logo_url ? (
                    <img src={b.logo_url} alt={b.name} className="h-8 w-auto" />
                  ) : (
                    <span className="font-editorial text-2xl text-[color:var(--ink)]">{b.name}</span>
                  )}
                </div>
                {b.recommended && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--evergreen)] px-2.5 py-1 text-[11px] font-bold text-white">
                    <Star size={11} /> Recommended
                  </span>
                )}
              </div>
              {b.blurb && <p className="mt-4 text-sm leading-relaxed text-[color:var(--ink-body)]">{b.blurb}</p>}
              <div className="mt-5 grid gap-2 text-[13px] text-[color:var(--ink-body)]">
                {b.platforms && <Row icon={Layers} label="Platforms" value={b.platforms} />}
                {b.regions && <Row icon={MapPin} label="Regions" value={b.regions} />}
                {b.min_deposit && <Row icon={Wallet} label="Min deposit" value={b.min_deposit} />}
                {b.spread_note && <Row icon={ShieldCheck} label="Spreads" value={b.spread_note} />}
              </div>
              <a
                href={brokerGoUrl(b.slug, "brokers-page", user?.id)}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid={`broker-open-${b.slug}`}
                className="btn-edge mt-6 justify-center"
              >
                Open an account with {b.name} <ArrowUpRight size={15} />
              </a>
            </div>
          ))}
        </div>
      )}
      <p className="mt-8 max-w-2xl text-[12px] text-[color:var(--ink-muted)]">
        Disclosure: these are affiliate/introducing-broker links. We may earn a commission when you open and fund an
        account — at no extra cost to you. Trading involves risk; only trade with capital you can afford to lose.
      </p>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={13} className="text-[color:var(--evergreen)]" />
      <span className="text-[color:var(--ink-muted)]">{label}:</span>
      <span className="font-medium text-[color:var(--ink)]">{value}</span>
    </div>
  );
}
